import React from 'react'

import Link from 'next/link'
import CheckLineIcon from 'remixicon-react/CheckLineIcon'
import ArrowLeftLineIcon from 'remixicon-react/ArrowLeftLineIcon'
import {Container, Col, Row, media} from 'styled-bootstrap-grid'

import styled from 'styled-components'
import {palette, switchProp} from 'styled-tools'

import {theme} from 'src/themes'
import {fluidRange} from 'src/utils/styles'
import {paths} from 'src/utils/paths'

export enum CheckoutStep {
  Receiver = 'receiver',
  Delivery = 'delivery',
  Payment = 'payment',
  Complete = 'complete',
}

enum StepStatus {
  Upcoming = 'upcoming',
  Current = 'current',
  Done = 'done',
}

interface Props {
  step: CheckoutStep
}

interface StepProps {
  status: StepStatus
}

interface BarProps {
  stepIndex: number
}

const WrapperMobile = styled.div`
  background-color: ${palette('background')};
  border-bottom: 1px solid ${palette('black20')};

  ${media.md`
    display: none;
  `}
`

const WrapperDesktop = styled.div`
  display: none;
  padding-top: 24px;
  background-color: ${palette('background')};
  border-bottom: 1px solid ${palette('black20')};

  ${media.md`
    display: block;
  `}
`

const BarWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: bottom;
`

// TODO: change to style.a when we resolve how to prevent user to go forward without completing a step
const Steps = styled.div`
  height: 56px;
  width: 100%;
  display: flex;
  cursor: pointer;
`

const Step = styled.div<StepProps>`
  width: calc(100% / 3);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  line-height: 1;
  position: relative;
  z-index: 1;
  color: ${switchProp(
    'status',
    {
      upcoming: palette('black20'),
      current: palette('secondary'),
      done: palette('black20'),
    },
    palette('black20'),
  )};
  ${fluidRange('font-size', '16px', '18px')}
`

const Bar = styled.div<BarProps>`
  position: absolute;
  height: 6px;
  bottom: -3px;
  left: 0;
  background-color: ${palette('red')};
  width: ${switchProp(
    'stepIndex',
    {
      1: 'calc(100% / 3)',
      2: 'calc(100% * 2 / 3)',
      3: '100%',
      4: '100%',
    },
    '0',
  )};
`

const CompleteIcon = styled.div`
  height: 20px;
  width: 20px;
  border-radius: 10px;
  background-color: ${palette('success')};
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  bottom: -10px;
  left: calc(50% - 10px);

  svg {
    display: block;
  }
`
const BackAnchor = styled.a`
  padding-left: 10px;
  display: flex;
  align-items: center;
  cursor: pointer;

  ${media.md`
      width: 60%;
    `}
`
const BackTxt = styled.p`
  padding-left: 10px;
  font-weight: 500;
`

const steps = [
  CheckoutStep.Receiver,
  CheckoutStep.Delivery,
  CheckoutStep.Payment,
  CheckoutStep.Complete,
]

const backBtnLabel: {[key: string]: string} = {
  [CheckoutStep.Receiver]: 'Til baka í körfu',
  [CheckoutStep.Delivery]: 'Til baka í móttakanda',
  [CheckoutStep.Payment]: 'Til baka í afhendingarmáta',
}

const backBtnPath: {[key: string]: string} = {
  [CheckoutStep.Receiver]: paths.cart,
  [CheckoutStep.Delivery]: paths.checkout,
  [CheckoutStep.Payment]: paths.delivery,
}

const barSteps: [string, string][] = [
  [paths.checkout, 'Móttakandi'],
  [paths.delivery, 'Afhendingarmáti'],
  [paths.payment, 'Greiðsla'],
]

function StatusBar({stepIndex}: BarProps): JSX.Element {
  function getStatus(index: number): StepStatus {
    if (index > stepIndex) {
      return StepStatus.Upcoming
    }
    if (index < stepIndex) {
      return StepStatus.Done
    }
    return StepStatus.Current
  }

  return (
    <BarWrapper>
      <Steps>
        {barSteps.map((step, i: number) => (
          // TODO: put Link back in when we resolve how to prevent user to go forward without completing a step
          // <Link href={step[0]} key={i} passHref>
          /* eslint-disable-next-line  react/no-array-index-key */
          <Step key={i} status={getStatus(i + 1)}>
            {step[1]}
            {i + 1 < stepIndex && (
              <CompleteIcon>
                <CheckLineIcon size={16} color={theme.palette.white} />
              </CompleteIcon>
            )}
          </Step>
          // </Link>
        ))}
      </Steps>
      <Bar stepIndex={stepIndex} />
    </BarWrapper>
  )
}

export default function CheckoutStatusBar({step}: Props): JSX.Element {
  const stepIndex = steps.indexOf(step) + 1
  return (
    <>
      <WrapperMobile>
        <StatusBar stepIndex={stepIndex} />
      </WrapperMobile>
      <WrapperDesktop>
        <Container>
          <Row alignItems="center">
            <Col col={1} md={3} lg={3.5}>
              {!!backBtnLabel[step] && (
                <Link href={backBtnPath[step]} passHref>
                  <BackAnchor>
                    {' '}
                    <ArrowLeftLineIcon color={theme.palette.red} />
                    <BackTxt>{backBtnLabel[step]}</BackTxt>
                  </BackAnchor>
                </Link>
              )}
            </Col>
            <Col col={10} md={6} lg={5}>
              <StatusBar stepIndex={stepIndex} />
            </Col>
            <Col col={1} md={3} lg={3.5} />
          </Row>
        </Container>
      </WrapperDesktop>
    </>
  )
}
